// Shared unit-aware cell value formatter used by table views that display
// measurement data returned by the backend in metric units.
//
// The helpers here delegate numeric conversion to UnitsHelper.formatUnitValue
// (which handles metric<->US conversion based on the user's preferred units)
// and centralize the column-key -> UnitType classification so every table has
// identical rendering rules.

import {
    formatUnitValue,
    getFormattedDate,
    resolveFlowUnitType,
    resolveUnitTypeFromLabel,
    UnitType,
} from './UnitsHelper';

/**
 * Context required to format a cell value correctly.
 * Propagated from the active unit configuration so the formatter can pick the
 * right UnitType for ambiguous columns (e.g. flowrate can be mass or volumetric,
 * unitOperationTargetValue can carry any unit type).
 */
export interface CellFormatContext {
    flowType?: string;
    unitOperationTargetValueUnit?: string;
}

/**
 * Options controlling fallback rendering.
 */
export interface FormatCellValueOptions {
    /** String used when a non-unit value is missing. Defaults to '' to match
     *  the existing ModelDataSampleList UX; callers like BlindTestList can
     *  pass '-' for an explicit placeholder. */
    emptyFallback?: string;
}

// Column keys (case-insensitive) rendered as formatted dates.
const DATE_COLUMN_KEYS = new Set(['createddate', 'modifieddate', 'updateddate']);

// Column keys (case-insensitive) that should never be treated as measurements
// even if they contain substrings like 'temp' (none currently, reserved for future).
const NON_MEASUREMENT_COLUMN_KEYS = new Set<string>([]);

/**
 * Strip a leading section prefix like "s1_", "s12_" so classification works on
 * grouped multi-section keys produced by the design-session pipeline.
 */
const stripSectionPrefix = (columnKey: string): string =>
    columnKey.replace(/^s\d+_/i, '');

/**
 * Determine which UnitType (if any) applies to a given column key.
 * Exported so consumers (tests, filter adapters, other table components) can
 * reuse the same mapping.
 */
export const getColumnUnitType = (
    columnKey: string,
    ctx: CellFormatContext
): UnitType | undefined => {
    const normalizedKey = stripSectionPrefix(columnKey).toLowerCase();
    if (NON_MEASUREMENT_COLUMN_KEYS.has(normalizedKey)) return undefined;
    if (normalizedKey === 'unitoperationtargetvalue') {
        // The shared resolver reports "no conversion" as null; this function's
        // published contract uses undefined, so translate at the boundary.
        return resolveUnitTypeFromLabel(ctx.unitOperationTargetValueUnit) ?? undefined;
    }
    if (normalizedKey.includes('flowrate')) return resolveFlowUnitType(ctx.flowType);
    if (normalizedKey.includes('pressure')) return 'pressure';
    if (normalizedKey.includes('temp')) return 'temperature';
    if (normalizedKey.includes('velocity')) return 'velocity';
    return undefined;
};

/**
 * True if the given column key is a known date column (regardless of any
 * "s1_" section prefix).
 */
export const isDateColumnKey = (columnKey: string): boolean =>
    DATE_COLUMN_KEYS.has(stripSectionPrefix(columnKey).toLowerCase());

/**
 * Format a cell value using the shared UnitsHelper.formatUnitValue so values
 * are converted between metric/US according to user preference, and missing
 * values / zeros are rendered consistently.
 *
 * Behavior:
 *  - Date columns (createdDate/modifiedDate/updatedDate) -> getFormattedDate.
 *  - Measurement columns -> formatUnitValue(..., round=false) which returns
 *    '-' for null/undefined/''.
 *  - Other columns -> String(value), or options.emptyFallback (defaults to '')
 *    for null/undefined/''.
 */
export const formatCellValue = (
    columnKey: string,
    value: any,
    ctx: CellFormatContext,
    options: FormatCellValueOptions = {}
): string => {
    const { emptyFallback = '' } = options;

    if (isDateColumnKey(columnKey)) {
        if (value === undefined || value === null || value === '') return emptyFallback;
        const iso = value instanceof Date ? value.toISOString() : String(value);
        return getFormattedDate(iso);
    }

    const unitType = getColumnUnitType(columnKey, ctx);
    if (unitType) {
        // round=false preserves the precision coming from the backend (metric),
        // matching the previous untransformed behaviour while adding conversion.
        // formatUnitValue already renders '-' for empty values.
        return formatUnitValue(value, unitType, false);
    }

    if (value === undefined || value === null || value === '') return emptyFallback;
    return String(value);
};
